import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicPageTemplate } from '@/components/layout';
import { useApePublicEvents } from '@/features/ape/useApe';
import { useShopSettings } from '@/features/shop/useShopSettings';
import { Store, ArrowRight, Sparkles } from 'lucide-react';
import type { PublicEvent } from '@/features/ape/types';

/**
 * Startseite — 1:1-Umsetzung von .planning/reference/design-bundle/project/Die Sizilianer.html.
 *
 * Klassen-Namen kommen aus src/styles/sizilianer-design.css.
 * HTML-Struktur ist identisch zum Design.
 */
export function Home() {
  const { t } = useTranslation();
  return (
    <PublicPageTemplate
      pageTitle={t('home.pageTitle')}
      description={t('home.pageDescription')}
    >
      {/* HERO */}
      <section className="hero" style={{ padding: '40px 56px 20px' }}>
        <img
          className="hero-composition"
          src="/img/Startseite.webp"
          /* Das Leerzeichen MUSS als %20 stehen. In srcset trennt Weissraum die
             URL vom Deskriptor — "/img/Startseite 720.webp 720w" liest der
             Browser als URL "/img/Startseite" mit den Deskriptoren "720.webp"
             und "720w". Der erste ist ungueltig, also verwirft er den ganzen
             Kandidaten. Die 720er-Datei war dadurch seit ihrer Einfuehrung tot:
             ausgeliefert wurde immer die 1402er (97 statt 61 KB). */
          srcSet="/img/Startseite%20720.webp 720w, /img/Startseite.webp 1402w"
          /* Die Anzeigebreite ist min(100vw - 112px, 320px): 56 px Innenabstand
             je Seite aus dem style unten, gedeckelt durch max-width in
             sizilianer-design.css. Das alte "100vw" log darum um mehr als das
             Doppelte und liess den Browser immer den groessten Kandidaten
             waehlen. */
          sizes="(max-width: 432px) calc(100vw - 112px), (max-width: 768px) 320px, 60vw"
          alt={t('home.hero.imageAlt')}
          /* Echte Dateimasse. Vorher stand hier 800x800 — der Browser leitete
             daraus ein Quadrat ab, reservierte es und klappte es nach dem Laden
             zusammen. */
          width={1402}
          height={640}
          fetchPriority="high"
        />

        <div className="hero-copy">
          <div className="benvenuti">{t('home.hero.kicker')}</div>
          <h1 className="title">{t('home.hero.title')}</h1>
          <div className="sub">{t('common.siteTagline')}</div>
          <div className="tri-rule">
            <span className="g"></span>
            <span className="w"></span>
            <span className="r"></span>
          </div>
          <p className="lede">
            {t('home.hero.ledeLine1')}
            <br />
            {t('home.hero.ledeLine2')}
          </p>
          <Link to="/reservieren" className="btn-primary">
            {t('home.hero.reserveCta')} <span className="arrow">→</span>
          </Link>
        </div>
      </section>

      {/* 08.R1 / K5: Google-Rating im Korridor 4,0–4,7 → dezent zeigen,
          verlinkt aufs echte Google-Profil (Places-ID). KEIN eigenes
          AggregateRating-Markup (10.SD2). Zahlen bei Gelegenheit pflegen. */}
      <div className="trust-line">
        <a
          href="https://search.google.com/local/reviews?placeid=ChIJmTlMjB7zuEcR_h_DdhMtiKo"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="stars" aria-hidden="true">★★★★★</span>
          {t('home.trustLine')}
        </a>
      </div>

      {/* UNSERE KÜCHE */}
      <section className="section-kueche">
        <img
          className="olive-left"
          src="/img/Botanische%20Olivenzweigzeichnung%20320.webp"
          srcSet="/img/Botanische%20Olivenzweigzeichnung%20320.webp 320w, /img/Botanische%20Olivenzweigzeichnung%20640.webp 640w"
          sizes="(max-width: 700px) 320px, 640px"
          width={320} height={253}
          alt="" loading="lazy" decoding="async" />
        <img
          className="olive-right"
          src="/img/Botanische%20Olivenzweigzeichnung%20320.webp"
          srcSet="/img/Botanische%20Olivenzweigzeichnung%20320.webp 320w, /img/Botanische%20Olivenzweigzeichnung%20640.webp 640w"
          sizes="(max-width: 700px) 320px, 640px"
          width={320} height={253}
          alt="" loading="lazy" decoding="async" />

        <div className="eyebrow">{t('home.kueche.eyebrow')}</div>
        <h2 className="section-title">{t('home.kueche.title')}</h2>
        <p className="section-sub">
          {t('home.kueche.sub')}
        </p>

        <div className="cards">
          <div className="card">
            <div className="ill">
              <img
                src="/img/Stillleben%20aus%20Tomaten%20und%20Kräutern%20320.webp"
                srcSet={`/img/Stillleben%20aus%20Tomaten%20und%20Kräutern%20320.webp 320w, /img/Stillleben%20aus%20Tomaten%20und%20Kräutern%20640.webp 640w`}
                sizes="(max-width: 700px) 160px, 200px"
                width={320}
                height={249}
                alt={t('home.kueche.cards.antipasti')} loading="lazy" decoding="async" />
            </div>
            <h3>{t('home.kueche.cards.antipasti')}</h3>
            <p>{t('home.kueche.cards.antipastiSub')}</p>
          </div>
          <div className="card">
            <div className="ill">
              <img
                src="/img/Pasta%20mit%20Kräutern%20und%20Kartoffeln%20320.webp"
                srcSet={`/img/Pasta%20mit%20Kräutern%20und%20Kartoffeln%20320.webp 320w, /img/Pasta%20mit%20Kräutern%20und%20Kartoffeln%20640.webp 640w`}
                sizes="(max-width: 700px) 160px, 200px"
                width={320}
                height={233}
                alt={t('home.kueche.cards.pasta')} loading="lazy" decoding="async" />
            </div>
            <h3>{t('home.kueche.cards.pasta')}</h3>
            <p>{t('home.kueche.cards.pastaSub')}</p>
          </div>
          <div className="card">
            <div className="ill">
              <img
                src="/img/Fischstillleben%20mit%20Kräutern%20und%20Zitrus%20320.webp"
                srcSet={`/img/Fischstillleben%20mit%20Kräutern%20und%20Zitrus%20320.webp 320w, /img/Fischstillleben%20mit%20Kräutern%20und%20Zitrus%20640.webp 640w`}
                sizes="(max-width: 700px) 160px, 200px"
                width={320}
                height={214}
                alt={t('home.kueche.cards.pesce')} loading="lazy" decoding="async" />
            </div>
            <h3>{t('home.kueche.cards.pesce')}</h3>
            <p>{t('home.kueche.cards.pesceSub')}</p>
          </div>
          <div className="card">
            <div className="ill">
              <img
                src="/img/Kunstvolle%20Cannoli%20mit%20Füllung%20und%20Garnitur%20320.webp"
                srcSet={`/img/Kunstvolle%20Cannoli%20mit%20Füllung%20und%20Garnitur%20320.webp 320w, /img/Kunstvolle%20Cannoli%20mit%20Füllung%20und%20Garnitur%20640.webp 640w`}
                sizes="(max-width: 700px) 160px, 200px"
                width={320}
                height={240}
                alt={t('home.kueche.cards.dolci')} loading="lazy" decoding="async" />
            </div>
            <h3>{t('home.kueche.cards.dolci')}</h3>
            <p>{t('home.kueche.cards.dolciSub')}</p>
          </div>
        </div>
      </section>

      {/* LA PUTIA — Sub-Site-Teaser */}
      <LaPutiaTeaser />

      {/* EVENTS */}
      <section className="section-events">
        <img
          className="church"
          src="/img/Barockkirche%20und%20Zitronen%20im%20Plaza%20360.webp"
          srcSet="/img/Barockkirche%20und%20Zitronen%20im%20Plaza%20360.webp 360w, /img/Barockkirche%20und%20Zitronen%20im%20Plaza%20640.webp 640w"
          sizes="(max-width: 700px) 360px, 640px"
          width={360} height={291}
          alt="" loading="lazy" decoding="async" />
        <img
          className="olive-tricolor"
          src="/img/Botanische%20Olivenzweigzeichnung%20320.webp"
          srcSet="/img/Botanische%20Olivenzweigzeichnung%20320.webp 320w, /img/Botanische%20Olivenzweigzeichnung%20640.webp 640w"
          sizes="(max-width: 700px) 320px, 640px"
          width={320} height={253}
          alt="" loading="lazy" decoding="async" />

        <div className="events-grid">
          <div className="events-copy">
            <div className="eyebrow">{t('home.events.eyebrow')}</div>
            <h2>{t('home.events.title')}</h2>
            <p>
              {t('home.events.text')}
            </p>
            <Link to="/events" className="btn-outline">
              {t('home.events.cta')} <span>→</span>
            </Link>
          </div>

          <HomeEventCards />
        </div>
      </section>

      {/* deco divider above footer */}
      <div className="deco-divider">
        <div className="seg g"></div>
        <div className="ornament">◇</div>
        <div className="seg r"></div>
      </div>
    </PublicPageTemplate>
  );
}

/**
 * Event-Karten im Home-Hero — zeigt die nächsten 2 öffentlichen Ape-Events
 * aus DB. Fallback: einladende "Coming Soon"-Karte wenn keine Events gepflegt.
 */
function HomeEventCards() {
  const { t } = useTranslation();
  const { data: events, isLoading } = useApePublicEvents();

  if (isLoading) {
    return (
      <div className="event-cards">
        <div className="event opacity-50">
          <p>{t('home.events.loading')}</p>
        </div>
      </div>
    );
  }

  const upcoming = (events ?? [])
    .filter((e) => new Date(e.date + 'T00:00:00') >= new Date(new Date().toDateString()))
    .slice(0, 2);

  if (upcoming.length === 0) {
    return (
      <div className="event-cards">
        <div className="event">
          <h4>{t('home.events.emptyTitle')}</h4>
          <p>{t('home.events.emptyText')}</p>
          <Link to="/events" className="more">
            {t('home.events.emptyCta')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="event-cards">
      {upcoming.map((e) => (
        <HomeEventCard key={e.id} event={e} />
      ))}
    </div>
  );
}

function HomeEventCard({ event }: { event: PublicEvent }) {
  const { t } = useTranslation();
  const date = new Date(event.date + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0') + '.';
  const month = date.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');

  return (
    <div className="event">
      <div className="date">
        <span className="d">{day}</span>
        <span className="m">{month}</span>
      </div>
      <h4>{event.title ?? t('home.events.fallbackTitle')}</h4>
      <p>{event.short_description ?? event.location_name}</p>
      <Link to="/events" className="more">
        {t('home.events.more')}
      </Link>
    </div>
  );
}

/**
 * Teaser-Card für La-Putia-Sub-Site auf der Home-Page.
 * Lädt shop_settings für Hero-Bild + Status.
 */
function LaPutiaTeaser() {
  const { t } = useTranslation();
  const { data: settings } = useShopSettings();
  const isOpen = settings?.is_open_for_business ?? false;
  const heroImage = settings?.hero_image_url;
  const tagline = settings?.tagline ?? t('home.laPutiaTeaser.taglineFallback');

  return (
    <section className="bg-paper-2 px-6 lg:px-12 py-12 lg:py-16 border-y border-rule">
      <div className="mx-auto max-w-5xl">
        <Link
          to="/la-putia"
          className="group block bg-paper border border-rule rounded-md overflow-hidden hover:border-gold transition-colors"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] items-stretch">
            {/* Bild */}
            <div className="aspect-[4/3] md:aspect-auto bg-paper-3 overflow-hidden">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={t('home.laPutiaTeaser.imageAlt')}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-it-green/20 via-paper-2 to-it-red/20 flex items-center justify-center">
                  <Store className="size-12 text-gold-2 opacity-60" />
                </div>
              )}
            </div>

            {/* Text */}
            <div className="p-6 lg:p-10 flex flex-col justify-center">
              <div className="font-sans text-2xs uppercase tracking-[0.32em] text-gold-2 font-semibold mb-2 inline-flex items-center gap-2">
                <Sparkles className="size-3.5" />
                {t('home.laPutiaTeaser.badge')}
              </div>
              <h2 className="font-script text-4xl lg:text-5xl text-ink-2 leading-tight mb-2">
                {t('home.laPutiaTeaser.title')}
              </h2>
              <p className="font-serif text-base lg:text-lg text-ink-soft mb-1">
                {tagline}
              </p>
              <p className="font-sans text-sm text-ink-soft leading-relaxed mt-3 max-w-lg">
                {t('home.laPutiaTeaser.text')}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-gold-2 font-sans text-sm font-semibold uppercase tracking-[0.22em] group-hover:gap-3 transition-[color,background-color,border-color,transform,box-shadow]">
                {isOpen
                  ? t('home.laPutiaTeaser.ctaOpen')
                  : t('home.laPutiaTeaser.ctaClosed')}
                <ArrowRight className="size-4" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
